/**
 * Single source of truth for the per-project "current batch id" carried in
 * sessionStorage. Both the writer (`ProjectFilesSection.setBatchId` after a
 * batch starts) and the reader (`useProposalProgress` in the header bar)
 * share these helpers so the key never drifts.
 *
 * Same-origin sessionStorage writes don't emit the cross-tab `storage`
 * event, so the writer fires a custom event that the reader subscribes
 * to. This replaces a perpetual 1s polling interval in the hook — single
 * deterministic notification per change.
 */ const BATCH_STORAGE_PREFIX = 'batch:';
const CHANGE_EVENT = 'thousand-sunny:batchIdChange';
function key(projectId) {
    return `${BATCH_STORAGE_PREFIX}${projectId}`;
}
export function readBatchId(projectId) {
    if (!projectId || typeof window === 'undefined') return null;
    try {
        return window.sessionStorage.getItem(key(projectId));
    } catch  {
        return null;
    }
}
export function writeBatchId(projectId, batchId) {
    if (typeof window === 'undefined') return;
    try {
        if (batchId) {
            window.sessionStorage.setItem(key(projectId), batchId);
        } else {
            window.sessionStorage.removeItem(key(projectId));
        }
    } catch  {
    // sessionStorage unavailable — fall through; consumers must tolerate
    // a stale read. In-memory React state in the writer still works.
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
        detail: {
            projectId,
            batchId
        }
    }));
}
/**
 * Subscribe to batchId changes for a specific project. Returns the
 * unsubscribe function so callers can wire it into `useEffect`.
 */ export function subscribeToBatchIdChanges(projectId, onChange) {
    if (typeof window === 'undefined') return ()=>undefined;
    const handler = (event)=>{
        const detail = event.detail;
        if (detail?.projectId === projectId) onChange(detail.batchId);
    };
    window.addEventListener(CHANGE_EVENT, handler);
    return ()=>window.removeEventListener(CHANGE_EVENT, handler);
}
